/**
 * MealFilterController
 * Handles client-side filtering of meal items on box builder pages
 * with sidebar UI and dynamic filter groups
 *
 * Filter Logic:
 * - Hauptkategorie: Single selection (radio-like behavior) OR "Alle" for no filter
 * - Sportkategorie: Single selection (radio-like behavior) OR "Alle" for no filter
 * - Filterkategorie (Präferenzen): Multiple selection (AND logic - meal must match ALL selected)
 */
class MealFilterController {
  constructor() {
    this.activeFilters = {
      hauptkategorie: 'all',
      sportkategorie: 'all',
      filterkategorie: []
    };

    this.filterContainer = null;
    this.sidebarOverlay = null;
    this.sidebar = null;
    this.mealItems = [];
    this.clearButton = null;
    this.toggleButton = null;
    this.sidebarFilterButton = null;
    this.mobileFilterButton = null;
    this.filterCountBadges = [];
  }

  init() {
    this.filterContainer = document.getElementById('meal-filters');
    this.sidebarOverlay = document.getElementById('filter-sidebar-overlay');
    this.sidebar = document.getElementById('filter-sidebar');

    if (!this.sidebarOverlay) {
      return;
    }

    this.mealItems = document.querySelectorAll('.meal-list-item');
    this.clearButton = document.getElementById('clear-filters');
    this.toggleButton = document.getElementById('filter-toggle-button');
    this.sidebarFilterButton = document.getElementById('sidebar-filter-button');
    this.mobileFilterButton = document.getElementById('mobile-filter-button');

    // Collect all filter count badges
    this.filterCountBadges = [
      document.getElementById('filter-count'),
      document.getElementById('sidebar-filter-count'),
      document.getElementById('mobile-filter-count')
    ].filter(el => el !== null);

    this.bindEvents();
    this.restoreFiltersFromURL();
  }

  bindEvents() {
    // Toggle button to open sidebar (original, if exists)
    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', () => this.openSidebar());
    }

    // Sidebar filter button (desktop - in "Deine Box" sidebar)
    if (this.sidebarFilterButton) {
      this.sidebarFilterButton.addEventListener('click', () => this.openSidebar());
    }

    // Mobile filter button (in bottom bar)
    if (this.mobileFilterButton) {
      this.mobileFilterButton.addEventListener('click', () => this.openSidebar());
    }

    // Close sidebar buttons
    const closeButton = document.getElementById('filter-sidebar-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeSidebar());
    }

    // Click on overlay to close
    if (this.sidebarOverlay) {
      this.sidebarOverlay.addEventListener('click', (e) => {
        if (e.target === this.sidebarOverlay) {
          this.closeSidebar();
        }
      });
    }

    // ESC key to close sidebar
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.sidebarOverlay.classList.contains('active')) {
        this.closeSidebar();
      }
    });

    // Hauptkategorie chips (single selection)
    const hauptChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="hauptkategorie"] .meal-filter-chip');
    hauptChips.forEach(chip => {
      chip.addEventListener('click', (e) => this.handleHauptkategorieClick(e));
    });

    // Sportkategorie chips (single selection)
    const sportChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="sportkategorie"] .meal-filter-chip');
    sportChips.forEach(chip => {
      chip.addEventListener('click', (e) => this.handleSportkategorieClick(e));
    });

    // Filterkategorie chips (multi selection)
    const filterChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="filterkategorie"] .meal-filter-chip');
    filterChips.forEach(chip => {
      chip.addEventListener('click', (e) => this.handleFilterkategorieClick(e));
    });

    // Apply filters button
    const applyButton = document.getElementById('filter-sidebar-apply');
    if (applyButton) {
      applyButton.addEventListener('click', () => this.closeSidebar());
    }

    // Reset filters button in sidebar
    const resetButton = document.getElementById('filter-sidebar-reset');
    if (resetButton) {
      resetButton.addEventListener('click', () => {
        this.clearAllFilters();
        this.closeSidebar();
      });
    }

    // Clear all filters button (in main UI)
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearAllFilters());
    }

    // Mobile sport chips (quick filters in mobile bottom bar)
    this.initMobileSportChips();
  }

  initMobileSportChips() {
    const mobileSportChips = document.querySelectorAll('.mobile-sport-chip');
    mobileSportChips.forEach(chip => {
      chip.addEventListener('click', (e) => this.handleMobileSportChipClick(e));
    });
  }

  handleMobileSportChipClick(event) {
    const chip = event.currentTarget;
    const value = chip.dataset.filterValue;

    // Toggle behavior: if already active, deselect (set to 'all')
    if (chip.classList.contains('active')) {
      // Remove active from clicked chip
      chip.classList.remove('active');
      this.activeFilters.sportkategorie = 'all';
    } else {
      // Remove active from all mobile sport chips
      document.querySelectorAll('.mobile-sport-chip').forEach(c => c.classList.remove('active'));
      // Add active to clicked chip
      chip.classList.add('active');
      this.activeFilters.sportkategorie = value;
    }

    // Also sync with sidebar chips
    this.syncSidebarSportChips();

    this.applyFilters();
    this.updateURL();
    this.updateClearButtonVisibility();
    this.updateFilterCountBadge();
  }

  syncSidebarSportChips() {
    // Sync sidebar sport chips with mobile chip selection
    const sidebarSportChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="sportkategorie"] .meal-filter-chip');
    sidebarSportChips.forEach(c => {
      if (c.dataset.filterValue === this.activeFilters.sportkategorie) {
        c.classList.add('active');
      } else {
        c.classList.remove('active');
      }
    });
  }

  syncMobileSportChips() {
    // Sync mobile sport chips with current filter state
    const mobileSportChips = document.querySelectorAll('.mobile-sport-chip');
    mobileSportChips.forEach(c => {
      if (c.dataset.filterValue === this.activeFilters.sportkategorie) {
        c.classList.add('active');
      } else {
        c.classList.remove('active');
      }
    });
  }

  openSidebar() {
    if (this.sidebarOverlay) {
      this.sidebarOverlay.classList.add('active');
      document.documentElement.classList.add('disabled-scroll');
    }
  }

  closeSidebar() {
    if (this.sidebarOverlay) {
      this.sidebarOverlay.classList.remove('active');
      document.documentElement.classList.remove('disabled-scroll');
    }
  }

  handleHauptkategorieClick(event) {
    const chip = event.currentTarget;

    // Ignore clicks on disabled chips
    if (chip.classList.contains('disabled')) {
      return;
    }

    const value = chip.dataset.filterValue;

    // Remove active from all hauptkategorie chips
    const hauptChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="hauptkategorie"] .meal-filter-chip');
    hauptChips.forEach(c => c.classList.remove('active'));

    // Add active to clicked chip
    chip.classList.add('active');

    this.activeFilters.hauptkategorie = value;
    this.applyFilters();
    this.updateURL();
    this.updateClearButtonVisibility();
    this.updateFilterCountBadge();
  }

  handleSportkategorieClick(event) {
    const chip = event.currentTarget;

    // Ignore clicks on disabled chips
    if (chip.classList.contains('disabled')) {
      return;
    }

    const value = chip.dataset.filterValue;

    // Remove active from all sportkategorie chips
    const sportChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="sportkategorie"] .meal-filter-chip');
    sportChips.forEach(c => c.classList.remove('active'));

    // Add active to clicked chip
    chip.classList.add('active');

    this.activeFilters.sportkategorie = value;
    this.applyFilters();
    this.updateURL();
    this.updateClearButtonVisibility();
    this.updateFilterCountBadge();

    // Sync mobile sport chips
    this.syncMobileSportChips();
  }

  handleFilterkategorieClick(event) {
    const chip = event.currentTarget;

    // Ignore clicks on disabled chips
    if (chip.classList.contains('disabled')) {
      return;
    }

    const value = chip.dataset.filterValue;

    // Toggle selection
    if (chip.classList.contains('active')) {
      chip.classList.remove('active');
      this.activeFilters.filterkategorie = this.activeFilters.filterkategorie.filter(v => v !== value);
    } else {
      chip.classList.add('active');
      this.activeFilters.filterkategorie.push(value);
    }

    this.applyFilters();
    this.updateURL();
    this.updateClearButtonVisibility();
    this.updateFilterCountBadge();
  }

  applyFilters() {
    let visibleCount = 0;

    this.mealItems.forEach(item => {
      const matchesHaupt = this.matchesHauptkategorie(item);
      const matchesSport = this.matchesSportkategorie(item);
      const matchesFilter = this.matchesFilterkategorie(item);

      // AND logic: must match all filter groups
      const isVisible = matchesHaupt && matchesSport && matchesFilter;

      if (isVisible) {
        item.style.display = '';
        visibleCount++;
      } else {
        item.style.display = 'none';
      }
    });

    this.updateNoResultsMessage(visibleCount);
    this.updateFilterAvailability();
  }

  /**
   * Check which filters would return results and disable those that wouldn't
   */
  updateFilterAvailability() {
    // Update Hauptkategorie chips
    const hauptChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="hauptkategorie"] .meal-filter-chip');
    hauptChips.forEach(chip => {
      const value = chip.dataset.filterValue;

      // "Alle" is always available
      if (value === 'all') {
        chip.classList.remove('disabled');
        return;
      }

      const wouldHaveResults = this.checkHauptkategorieAvailability(value);
      chip.classList.toggle('disabled', !wouldHaveResults);
    });

    // Update Sportkategorie chips
    const sportChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="sportkategorie"] .meal-filter-chip');
    sportChips.forEach(chip => {
      const value = chip.dataset.filterValue;

      // "Alle" is always available
      if (value === 'all') {
        chip.classList.remove('disabled');
        return;
      }

      const wouldHaveResults = this.checkSportkategorieAvailability(value);
      chip.classList.toggle('disabled', !wouldHaveResults);
    });

    // Update Filterkategorie chips
    const filterChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="filterkategorie"] .meal-filter-chip');
    filterChips.forEach(chip => {
      const value = chip.dataset.filterValue;

      // If this filter is already active, don't disable it (user can deselect)
      if (this.activeFilters.filterkategorie.includes(value)) {
        chip.classList.remove('disabled');
        return;
      }

      const wouldHaveResults = this.checkFilterkategorieAvailability(value);
      chip.classList.toggle('disabled', !wouldHaveResults);
    });
  }

  /**
   * Check if selecting a Hauptkategorie would return any results
   */
  checkHauptkategorieAvailability(hauptValue) {
    return Array.from(this.mealItems).some(item => {
      const itemKategorie = item.dataset.hauptkategorie || '';
      const matchesHaupt = itemKategorie === hauptValue;

      // Also check against current Sportkategorie (now comma-separated)
      const itemSports = (item.dataset.sportkategorie || '').split(',').filter(s => s);
      const matchesSport = this.activeFilters.sportkategorie === 'all' ||
        itemSports.includes(this.activeFilters.sportkategorie);

      // Also check against current Filterkategorie
      if (this.activeFilters.filterkategorie.length === 0) {
        return matchesHaupt && matchesSport;
      }

      const itemTags = (item.dataset.filterkategorie || '').split(',').filter(t => t);
      const matchesFilter = this.activeFilters.filterkategorie.every(filter => itemTags.includes(filter));

      return matchesHaupt && matchesSport && matchesFilter;
    });
  }

  /**
   * Check if selecting a Sportkategorie would return any results
   */
  checkSportkategorieAvailability(sportValue) {
    return Array.from(this.mealItems).some(item => {
      const itemSports = (item.dataset.sportkategorie || '').split(',').filter(s => s);
      const matchesSport = itemSports.includes(sportValue);

      // Also check against current Hauptkategorie
      const matchesHaupt = this.activeFilters.hauptkategorie === 'all' ||
        (item.dataset.hauptkategorie || '') === this.activeFilters.hauptkategorie;

      // Also check against current Filterkategorie
      if (this.activeFilters.filterkategorie.length === 0) {
        return matchesHaupt && matchesSport;
      }

      const itemTags = (item.dataset.filterkategorie || '').split(',').filter(t => t);
      const matchesFilter = this.activeFilters.filterkategorie.every(filter => itemTags.includes(filter));

      return matchesHaupt && matchesSport && matchesFilter;
    });
  }

  /**
   * Check if adding a Filterkategorie would return any results
   */
  checkFilterkategorieAvailability(filterValue) {
    // Create hypothetical filter array with this new filter added
    const hypotheticalFilters = [...this.activeFilters.filterkategorie, filterValue];

    return Array.from(this.mealItems).some(item => {
      // Check against current Hauptkategorie
      const matchesHaupt = this.activeFilters.hauptkategorie === 'all' ||
        (item.dataset.hauptkategorie || '') === this.activeFilters.hauptkategorie;

      if (!matchesHaupt) return false;

      // Check against current Sportkategorie (now comma-separated)
      const itemSports = (item.dataset.sportkategorie || '').split(',').filter(s => s);
      const matchesSport = this.activeFilters.sportkategorie === 'all' ||
        itemSports.includes(this.activeFilters.sportkategorie);

      if (!matchesSport) return false;

      // Check if item has ALL hypothetical filters
      const itemTags = (item.dataset.filterkategorie || '').split(',').filter(t => t);
      return hypotheticalFilters.every(filter => itemTags.includes(filter));
    });
  }

  matchesHauptkategorie(item) {
    if (this.activeFilters.hauptkategorie === 'all') {
      return true;
    }

    const itemKategorie = item.dataset.hauptkategorie || '';
    return itemKategorie === this.activeFilters.hauptkategorie;
  }

  matchesSportkategorie(item) {
    if (this.activeFilters.sportkategorie === 'all') {
      return true;
    }

    const itemSports = (item.dataset.sportkategorie || '').split(',').filter(s => s);
    return itemSports.includes(this.activeFilters.sportkategorie);
  }

  matchesFilterkategorie(item) {
    if (this.activeFilters.filterkategorie.length === 0) {
      return true;
    }

    const itemTags = (item.dataset.filterkategorie || '').split(',').filter(t => t);

    // AND logic: item must have ALL selected filter tags
    return this.activeFilters.filterkategorie.every(filter => itemTags.includes(filter));
  }

  updateNoResultsMessage(visibleCount) {
    let noResultsEl = document.getElementById('meal-filter-no-results');

    if (visibleCount === 0) {
      if (!noResultsEl) {
        noResultsEl = document.createElement('div');
        noResultsEl.id = 'meal-filter-no-results';
        noResultsEl.className = 'meal-filter-no-results';

        const messageP = document.createElement('p');
        messageP.textContent = 'Keine Gerichte gefunden. Bitte andere Filter wählen.';
        noResultsEl.appendChild(messageP);

        const mealList = document.querySelector('.build-a-box__meal-list');
        if (mealList) {
          mealList.parentNode.insertBefore(noResultsEl, mealList.nextSibling);
        }
      }
      noResultsEl.style.display = 'block';
    } else if (noResultsEl) {
      noResultsEl.style.display = 'none';
    }
  }

  clearAllFilters() {
    // Reset hauptkategorie to "all"
    this.activeFilters.hauptkategorie = 'all';
    const hauptChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="hauptkategorie"] .meal-filter-chip');
    hauptChips.forEach(chip => {
      chip.classList.toggle('active', chip.dataset.filterValue === 'all');
    });

    // Reset sportkategorie to "all"
    this.activeFilters.sportkategorie = 'all';
    const sportChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="sportkategorie"] .meal-filter-chip');
    sportChips.forEach(chip => {
      chip.classList.toggle('active', chip.dataset.filterValue === 'all');
    });

    // Clear all filterkategorie filters
    this.activeFilters.filterkategorie = [];
    const filterChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="filterkategorie"] .meal-filter-chip');
    filterChips.forEach(chip => chip.classList.remove('active'));

    // Clear mobile sport chips
    this.syncMobileSportChips();

    this.applyFilters();
    this.updateURL();
    this.updateClearButtonVisibility();
    this.updateFilterCountBadge();
  }

  updateClearButtonVisibility() {
    if (!this.clearButton) return;

    const hasActiveFilters =
      this.activeFilters.hauptkategorie !== 'all' ||
      this.activeFilters.sportkategorie !== 'all' ||
      this.activeFilters.filterkategorie.length > 0;

    this.clearButton.classList.toggle('hidden', !hasActiveFilters);
  }

  updateFilterCountBadge() {
    let count = 0;
    if (this.activeFilters.hauptkategorie !== 'all') count++;
    if (this.activeFilters.sportkategorie !== 'all') count++;
    count += this.activeFilters.filterkategorie.length;

    // Update all filter count badges
    this.filterCountBadges.forEach(badge => {
      if (count > 0) {
        badge.textContent = count;
        badge.style.display = '';
      } else {
        badge.style.display = 'none';
      }
    });
  }

  updateURL() {
    const params = new URLSearchParams(window.location.search);

    // Update hauptkategorie param
    if (this.activeFilters.hauptkategorie !== 'all') {
      params.set('kategorie', this.activeFilters.hauptkategorie);
    } else {
      params.delete('kategorie');
    }

    // Update sportkategorie param
    if (this.activeFilters.sportkategorie !== 'all') {
      params.set('sport', this.activeFilters.sportkategorie);
    } else {
      params.delete('sport');
    }

    // Update filterkategorie params
    if (this.activeFilters.filterkategorie.length > 0) {
      params.set('filter', this.activeFilters.filterkategorie.join(','));
    } else {
      params.delete('filter');
    }

    const newURL = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    history.replaceState(null, '', newURL);
  }

  restoreFiltersFromURL() {
    const params = new URLSearchParams(window.location.search);

    // Restore hauptkategorie (case insensitive)
    const kategorie = params.get('kategorie');
    if (kategorie) {
      const kategorieLower = kategorie.toLowerCase();
      this.activeFilters.hauptkategorie = kategorieLower;
      const hauptChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="hauptkategorie"] .meal-filter-chip');
      hauptChips.forEach(chip => {
        chip.classList.toggle('active', chip.dataset.filterValue === kategorieLower);
      });
    }

    // Restore sportkategorie (case insensitive)
    const sport = params.get('sport');
    if (sport) {
      const sportLower = sport.toLowerCase();
      this.activeFilters.sportkategorie = sportLower;
      const sportChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="sportkategorie"] .meal-filter-chip');
      sportChips.forEach(chip => {
        chip.classList.toggle('active', chip.dataset.filterValue === sportLower);
      });
    }

    // Restore filterkategorie filters (case insensitive)
    const filter = params.get('filter');
    if (filter) {
      this.activeFilters.filterkategorie = filter.split(',').map(f => f.toLowerCase());
      const filterChips = this.sidebarOverlay.querySelectorAll('[data-filter-group="filterkategorie"] .meal-filter-chip');
      filterChips.forEach(chip => {
        chip.classList.toggle('active', this.activeFilters.filterkategorie.includes(chip.dataset.filterValue));
      });
    }

    this.applyFilters();
    this.updateClearButtonVisibility();
    this.updateFilterCountBadge();

    // Sync mobile sport chips with restored state
    this.syncMobileSportChips();
  }
}
